import { useState, useCallback } from 'react';
import type { BlogPost, Blog, Site, ImageCrop } from '../../schemas/validation';
import { useToast, type ToastItem, type ToastOptions } from '../../components/Toast';
import { useCanUseRoary } from '../../hooks/useCanUseRoary';

export interface BlogPostEditorState {
  // Data state
  site: Site | null;
  setSite: (site: Site | null) => void;
  post: BlogPost | null;
  setPost: (post: BlogPost | null) => void;
  blog: Blog | null;
  setBlog: (blog: Blog | null) => void;

  // Form state
  title: string;
  setTitle: (title: string) => void;
  slug: string;
  setSlug: (slug: string) => void;
  content: string;
  setContent: (content: string) => void;
  excerpt: string;
  setExcerpt: (excerpt: string) => void;
  featuredImageUrl: string;
  setFeaturedImageUrl: (url: string) => void;
  featuredImageCrop: ImageCrop | undefined;
  setFeaturedImageCrop: (crop: ImageCrop | undefined) => void;
  authorName: string;
  setAuthorName: (name: string) => void;
  tags: string[];
  setTags: (tags: string[]) => void;
  newTag: string;
  setNewTag: (tag: string) => void;
  seoTitle: string;
  setSeoTitle: (title: string) => void;
  seoDescription: string;
  setSeoDescription: (desc: string) => void;
  seoImageUrl: string;
  setSeoImageUrl: (url: string) => void;

  // Loading states
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  isSaving: boolean;
  setIsSaving: (saving: boolean) => void;
  isPublishing: boolean;
  setIsPublishing: (publishing: boolean) => void;
  isDeleting: boolean;
  setIsDeleting: (deleting: boolean) => void;
  isUploadingImage: boolean;
  setIsUploadingImage: (uploading: boolean) => void;

  // UI state
  showDeleteModal: boolean;
  setShowDeleteModal: (show: boolean) => void;
  showSeoPanel: boolean;
  setShowSeoPanel: (show: boolean) => void;
  showSeoAnalysis: boolean;
  setShowSeoAnalysis: (show: boolean) => void;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (has: boolean) => void;

  // Toast state (shared Toast system)
  toasts: ToastItem[];
  removeToast: (id: string) => void;

  // Media Library state
  showMediaLibrary: boolean;
  setShowMediaLibrary: (show: boolean) => void;

  // Preview state
  showPreview: boolean;
  setShowPreview: (show: boolean) => void;

  // Helper functions
  showNotification: (message: string, type: 'success' | 'error', options?: ToastOptions) => void;
  handleFieldChange: (setter: (value: string) => void, value: string) => void;
}

export const useBlogPostEditorState = (): BlogPostEditorState => {
  // Data state
  const [site, setSite] = useState<Site | null>(null);
  const [post, setPost] = useState<BlogPost | null>(null);
  const [blog, setBlog] = useState<Blog | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [featuredImageUrl, setFeaturedImageUrl] = useState('');
  const [featuredImageCrop, setFeaturedImageCrop] = useState<ImageCrop | undefined>(undefined);
  const [authorName, setAuthorName] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoImageUrl, setSeoImageUrl] = useState('');

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // UI state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSeoPanel, setShowSeoPanel] = useState(false);
  const [showSeoAnalysis, setShowSeoAnalysis] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Toast state (shared Toast system)
  const canUseRoaryUi = useCanUseRoary();
  const { toasts, showToast, removeToast } = useToast(canUseRoaryUi);

  // Media Library state
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);

  const showNotification = useCallback(
    (message: string, type: 'success' | 'error', options?: ToastOptions) => {
      showToast(message, type, options);
    },
    [showToast]
  );

  const handleFieldChange = useCallback((setter: (value: string) => void, value: string) => {
    setter(value);
    setHasUnsavedChanges(true);
  }, []);

  return {
    site,
    setSite,
    post,
    setPost,
    blog,
    setBlog,
    title,
    setTitle,
    slug,
    setSlug,
    content,
    setContent,
    excerpt,
    setExcerpt,
    featuredImageUrl,
    setFeaturedImageUrl,
    featuredImageCrop,
    setFeaturedImageCrop,
    authorName,
    setAuthorName,
    tags,
    setTags,
    newTag,
    setNewTag,
    seoTitle,
    setSeoTitle,
    seoDescription,
    setSeoDescription,
    seoImageUrl,
    setSeoImageUrl,
    isLoading,
    setIsLoading,
    isSaving,
    setIsSaving,
    isPublishing,
    setIsPublishing,
    isDeleting,
    setIsDeleting,
    isUploadingImage,
    setIsUploadingImage,
    showDeleteModal,
    setShowDeleteModal,
    showSeoPanel,
    setShowSeoPanel,
    showSeoAnalysis,
    setShowSeoAnalysis,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    toasts,
    removeToast,
    showMediaLibrary,
    setShowMediaLibrary,
    showPreview,
    setShowPreview,
    showNotification,
    handleFieldChange,
  };
};
